/**
 * Общая логика редактирования дневного и месячного отчета аптеки (клиентская часть).
 *
 * Структура полей и формулы приходят с сервера (GET /api/v2/reports/edit/day|month), здесь они только
 * применяются для мгновенного пересчета на экране. Итоговый расчет и запись выполняет сервер
 * (services/reportCalc.js), поэтому логика вычислений здесь повторяет серверную один в один.
 * Формулы разбираются собственным парсером, без eval / new Function.
 */

export type FieldKind = 'meta' | 'input' | 'carry' | 'formula' | 'external';

export interface EditField {
  name: string;
  label: string;
  group: string;
  type: string;
  kind: FieldKind;
  important: boolean;
  expr: string | null;
}

/** Значения полей одного дня: имя поля -> число */
export type DayValues = Record<string, number>;

/** Остаток в кассе на конец дня: от него считается остаток на начало следующего дня */
export const END_FIELD = 'cash_balance_end_of_day';

const INT_MIN = -2147483648;
const INT_MAX = 2147483647;

export function isIntField(field: EditField): boolean {
  return field.type === 'INT';
}

// ---------------------------------------------------------------------------
// Разбор арифметических выражений
// ---------------------------------------------------------------------------

type Evaluator = (values: DayValues) => number;

interface Token {
  type: 'num' | 'id' | 'op';
  value: string;
}

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  const re = /\s*(?:(\d+(?:\.\d+)?)|([A-Za-z_]\w*)|([-+*/()]))/g;
  let pos = 0;

  while (pos < source.length) {
    re.lastIndex = pos;
    const m = re.exec(source);
    if (!m || m.index !== pos) {
      if (source.slice(pos).trim() === '') break;
      throw new Error(`Недопустимый символ в формуле «${source}»`);
    }
    if (m[1] !== undefined) tokens.push({ type: 'num', value: m[1] });
    else if (m[2] !== undefined) tokens.push({ type: 'id', value: m[2] });
    else tokens.push({ type: 'op', value: m[3] });
    pos = re.lastIndex;
  }
  return tokens;
}

function compileExpression(source: string): Evaluator {
  const tokens = tokenize(source);
  let pos = 0;

  const peekOp = (...ops: string[]): string | null => {
    const t = tokens[pos];
    return t && t.type === 'op' && ops.indexOf(t.value) >= 0 ? t.value : null;
  };

  const parseFactor = (): Evaluator => {
    const t = tokens[pos];
    if (!t) throw new Error(`Неполная формула «${source}»`);

    if (t.type === 'op' && (t.value === '-' || t.value === '+')) {
      pos++;
      const inner = parseFactor();
      return t.value === '-' ? (v) => -inner(v) : inner;
    }
    if (t.type === 'op' && t.value === '(') {
      pos++;
      const inner = parseExpr();
      if (!peekOp(')')) throw new Error(`Не закрыта скобка в формуле «${source}»`);
      pos++;
      return inner;
    }
    if (t.type === 'num') {
      pos++;
      const value = Number(t.value);
      return () => value;
    }
    if (t.type === 'id') {
      pos++;
      const name = t.value;
      return (v) => {
        const n = Number(v[name]);
        return Number.isFinite(n) ? n : 0;
      };
    }
    throw new Error(`Неожиданный символ «${t.value}» в формуле «${source}»`);
  };

  const parseTerm = (): Evaluator => {
    let left = parseFactor();
    let op: string | null;
    while ((op = peekOp('*', '/'))) {
      pos++;
      const l = left;
      const r = parseFactor();
      left = op === '*' ? (v) => l(v) * r(v) : (v) => l(v) / r(v);
    }
    return left;
  };

  function parseExpr(): Evaluator {
    let left = parseTerm();
    let op: string | null;
    while ((op = peekOp('+', '-'))) {
      pos++;
      const l = left;
      const r = parseTerm();
      left = op === '+' ? (v) => l(v) + r(v) : (v) => l(v) - r(v);
    }
    return left;
  }

  const fn = parseExpr();
  if (pos < tokens.length) throw new Error(`Лишние символы в конце формулы «${source}»`);
  return fn;
}

// ---------------------------------------------------------------------------
// Калькулятор отчета
// ---------------------------------------------------------------------------

export interface Calculator {
  /**
   * Пересчитывает вычисляемые поля дня.
   * prevEnd - остаток на конец предыдущего дня (null - предыдущей записи нет, остаток на начало остается как есть).
   */
  compute(values: DayValues, prevEnd: number | null): DayValues;
}

function normalize(field: EditField, value: number): number {
  const n = Number.isFinite(value) ? value : 0;
  return isIntField(field) ? Math.round(n) : n;
}

export function createCalculator(fields: EditField[], computeOrder: string[]): Calculator {
  const byName: Record<string, EditField> = {};
  fields.forEach((f) => { byName[f.name] = f; });

  const steps = computeOrder.map((name) => {
    const field = byName[name];
    if (!field || !field.expr) throw new Error(`Для поля «${name}» не задана формула`);
    return { field, fn: compileExpression(field.expr) };
  });
  const carry = fields.filter((f) => f.kind === 'carry')[0] || null;

  return {
    compute(values, prevEnd) {
      const out: DayValues = { ...values };
      if (carry && prevEnd !== null) out[carry.name] = normalize(carry, prevEnd);
      steps.forEach(({ field, fn }) => {
        out[field.name] = normalize(field, fn(out));
      });
      return out;
    }
  };
}

/** Первое поле, значение которого не помещается в тип INT таблицы (сервер откажет в сохранении) */
export function findOverflow(fields: EditField[], values: DayValues): EditField | null {
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i];
    if (f.kind === 'meta' || !isIntField(f)) continue;
    const v = values[f.name];
    if (v < INT_MIN || v > INT_MAX) return f;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Числа: отображение и ввод
// ---------------------------------------------------------------------------

const NBSP = ' ';

export function formatNumber(value: number, field?: EditField): string {
  if (!Number.isFinite(value)) return '0';
  const digits = field && !isIntField(field) ? 2 : 0;
  const fixed = Math.abs(value).toFixed(digits);
  const parts = fixed.split('.');
  const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  const sign = value < 0 && Number(fixed) !== 0 ? '-' : '';
  return sign + int + (parts[1] ? `.${parts[1]}` : '');
}

export type ParseResult = { ok: true; value: number } | { ok: false; message: string };

/** Разбор введенного пользователем текста (пробелы-разделители, запятая как точка, пустая строка = 0) */
export function parseNumberInput(text: string, field: EditField): ParseResult {
  const cleaned = text
    .replace(/[\s  ]/g, '')
    .replace(/[−–]/g, '-')
    .replace(',', '.');

  if (cleaned === '' || cleaned === '-') return { ok: true, value: 0 };
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return { ok: false, message: 'введите число' };

  const n = Number(cleaned);
  if (isIntField(field)) {
    if (!Number.isInteger(n)) return { ok: false, message: 'допускаются только целые числа' };
    if (n < INT_MIN || n > INT_MAX) return { ok: false, message: 'число слишком большое' };
    return { ok: true, value: n === 0 ? 0 : n };
  }
  const rounded = Math.round(n * 100) / 100;
  return { ok: true, value: rounded === 0 ? 0 : rounded };
}

// ---------------------------------------------------------------------------
// Группы полей
// ---------------------------------------------------------------------------

export interface FieldGroup {
  name: string;
  fields: EditField[];
}

/** Группы в порядке появления в table_info (Касса, Банк, Статистика...), поля внутри группы - в порядке table_info */
export function groupFields(fields: EditField[]): FieldGroup[] {
  const groups: FieldGroup[] = [];
  fields.forEach((f) => {
    const name = f.group || 'Прочее';
    let group = groups.filter((g) => g.name === name)[0];
    if (!group) {
      group = { name, fields: [] };
      groups.push(group);
    }
    group.fields.push(f);
  });
  return groups;
}

// ---------------------------------------------------------------------------
// Даты (YYYY-MM-DD, часовой пояс отчетов Asia/Almaty - как на сервере)
// ---------------------------------------------------------------------------

const reportDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Almaty',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

export const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];

const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

export function getTodayStr(): string {
  return reportDateFormatter.format(new Date());
}

export function getYesterdayStr(): string {
  const [y, m, d] = getTodayStr().split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
}

export function getCurrentMonthStr(): string {
  return getTodayStr().slice(0, 7);
}

export function getPrevMonthStr(): string {
  const [y, m] = getTodayStr().split('-').map(Number);
  return new Date(Date.UTC(y, m - 2, 1)).toISOString().slice(0, 7);
}

export function isYmd(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/** 2026-09-03 -> 03.09.2026 */
export function formatDateRu(ymd: string): string {
  const [y, m, d] = ymd.split('-');
  return `${d}.${m}.${y}`;
}

/** 2026-09 -> Сентябрь 2026 */
export function formatMonthRu(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return `${MONTH_NAMES[m - 1] || ym} ${y}`;
}

/** Номер дня недели (0 - воскресенье) для даты YYYY-MM-DD */
export function weekdayIndex(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

export function weekdayShort(ymd: string): string {
  return WEEKDAYS[weekdayIndex(ymd)];
}

export function isWeekend(ymd: string): boolean {
  const i = weekdayIndex(ymd);
  return i === 0 || i === 6;
}
